import React, { useState, useMemo } from 'react';
import {
    Button,
    Collapse,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    TextField,
    Box,
    Tabs,
    Tab,
    Card,
    CardContent
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const GameDiagnostics = ({ gamesList }) => {
    const [expanded, setExpanded] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);

    const toggleExpanded = () => {
        setExpanded(!expanded);
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const stats = useMemo(() => {
        if (!gamesList.length) return {};

        // Count unique values
        const uniqueModuleClientCombos = new Set();
        const uniqueLaunchIDs = new Set();
        const moduleClientToGames = new Map();
        const launchIDToGames = new Map();

        // Track all games by moduleId-clientId and launchID
        gamesList.forEach(game => {
            const combo = `${game.moduleId}-${game.clientId}`;

            // Track moduleId-clientId combinations
            if (!moduleClientToGames.has(combo)) {
                moduleClientToGames.set(combo, []);
            }
            moduleClientToGames.get(combo).push(game);

            // Track launch IDs
            if (game.launchID) {
                if (!launchIDToGames.has(game.launchID)) {
                    launchIDToGames.set(game.launchID, []);
                }
                launchIDToGames.get(game.launchID).push(game);
            }
        });

        // Find duplicates
        const duplicateModuleClientCombos = new Map(
            [...moduleClientToGames.entries()].filter(([_, games]) => games.length > 1)
        );

        const duplicateLaunchIDs = new Map(
            [...launchIDToGames.entries()].filter(([_, games]) => games.length > 1)
        );

        // Count unique values
        moduleClientToGames.forEach((_, key) => uniqueModuleClientCombos.add(key));
        launchIDToGames.forEach((_, key) => uniqueLaunchIDs.add(key));

        // Find games with same name but different launch IDs
        const gamesByName = {};
        gamesList.forEach(game => {
            if (!gamesByName[game.gameName]) {
                gamesByName[game.gameName] = [];
            }
            gamesByName[game.gameName].push(game);
        });

        const sameNameDiffLaunchID = Object.entries(gamesByName)
            .filter(([_, games]) => {
                if (games.length <= 1) return false;
                const launchIDs = new Set(games.map(g => g.launchID));
                return launchIDs.size > 1;
            })
            .map(([name, games]) => ({
                name,
                games
            }));

        // Filter by search term
        const filterBySearchTerm = (entries) => {
            if (!searchTerm) return entries;

            return entries.filter(([key, games]) => {
                return key.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    games.some(g =>
                        g.gameName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        g.moduleId.toString().includes(searchTerm) ||
                        g.clientId.toString().includes(searchTerm) ||
                        (g.provider && g.provider.toLowerCase().includes(searchTerm.toLowerCase()))
                    );
            });
        };

        // Apply search filter
        const filteredDuplicateModuleClientCombos = filterBySearchTerm(
            Array.from(duplicateModuleClientCombos.entries())
        );

        const filteredDuplicateLaunchIDs = filterBySearchTerm(
            Array.from(duplicateLaunchIDs.entries())
        );

        // Missing games check - if specified in search
        const specificGamesToCheck = [
            'bananzaV94', 'bananzaV94Desktop',
            'empireShieldsV92', 'empireShieldsV92Desktop'
        ];

        const missingGamesCheck = searchTerm ? specificGamesToCheck
            .filter(game => game.toLowerCase().includes(searchTerm.toLowerCase()))
            .map(game => {
                const found = gamesList.some(g =>
                    g.launchID && g.launchID.toLowerCase() === game.toLowerCase()
                );
                return { name: game, found };
            }) : [];

        return {
            totalGames: gamesList.length,
            uniqueModuleClientCombos: uniqueModuleClientCombos.size,
            duplicateModuleClientComboCounts: duplicateModuleClientCombos.size,
            uniqueLaunchIDs: uniqueLaunchIDs.size,
            duplicateLaunchIDCounts: duplicateLaunchIDs.size,
            sameNameDiffLaunchIDCount: sameNameDiffLaunchID.length,
            duplicateModuleClientCombos: filteredDuplicateModuleClientCombos,
            duplicateLaunchIDs: filteredDuplicateLaunchIDs,
            missingGamesCheck,
            searchTerm
        };
    }, [gamesList, searchTerm]);

    // Render list items for duplicates
    const renderDuplicateItems = (duplicates, itemType) => {
        if (!duplicates.length) {
            return (
                <Typography variant="body2" sx={{ fontStyle: 'italic', p: 2 }}>
                    No {searchTerm ? "matching " : ""}duplicates found
                </Typography>
            );
        }

        return (
            <List>
                {duplicates.map(([key, games]) => (
                    <React.Fragment key={key}>
                        <ListItem>
                            <ListItemText
                                primary={`${itemType}: ${key}`}
                                secondary={
                                    <React.Fragment>
                                        {games.map((game, i) => (
                                            <div key={i}>
                                                Game {i + 1}: {game.gameName} | Module ID: {game.moduleId} | Client ID: {game.clientId} | Provider: {game.provider}
                                            </div>
                                        ))}
                                    </React.Fragment>
                                }
                            />
                        </ListItem>
                        <Divider />
                    </React.Fragment>
                ))}
                {duplicates.length > 20 && !searchTerm && (
                    <Typography variant="body2" sx={{ mt: 1, p: 1, fontStyle: 'italic' }}>
                        Showing first 20 of {duplicates.length} duplicates. Use search to find specific items.
                    </Typography>
                )}
            </List>
        );
    };

    // Render missing games check
    const renderMissingGamesCheck = () => {
        if (!stats.missingGamesCheck || stats.missingGamesCheck.length === 0) {
            return (
                <Typography variant="body2" sx={{ fontStyle: 'italic', p: 2 }}>
                    Enter a search term to check for specific missing games
                </Typography>
            );
        }

        return (
            <List>
                {stats.missingGamesCheck.map((item) => (
                    <ListItem key={item.name}>
                        <ListItemText
                            primary={item.name}
                            secondary={item.found ? "✅ Found in game list" : "❌ Missing from game list"}
                            secondaryTypographyProps={{
                                color: item.found ? 'success.main' : 'error.main',
                                fontWeight: 'bold'
                            }}
                        />
                    </ListItem>
                ))}
            </List>
        );
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                    Game List Diagnostics
                </Typography>
                <Button
                    onClick={toggleExpanded}
                    endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                    {expanded ? 'Hide Details' : 'Show Details'}
                </Button>
            </Box>

            <Card variant="outlined" sx={{ mb: 1, mt: 1 }}>
                <CardContent sx={{ py: 1 }}>
                    <Typography>
                        Total Games: <b>{stats.totalGames}</b> | Unique Module-Client Combinations: <b>{stats.uniqueModuleClientCombos}</b> | Unique Launch IDs: <b>{stats.uniqueLaunchIDs}</b>
                    </Typography>

                    <Box sx={{ display: 'flex', mt: 1, flexWrap: 'wrap', gap: 2 }}>
                        <Typography variant="body2" sx={{
                            fontWeight: 'bold',
                            color: stats.duplicateModuleClientComboCounts > 0 ? 'error.main' : 'success.main'
                        }}>
                            Duplicate Module-Client Combinations: {stats.duplicateModuleClientComboCounts}
                        </Typography>

                        <Typography variant="body2" sx={{
                            fontWeight: 'bold',
                            color: stats.duplicateLaunchIDCounts > 0 ? 'error.main' : 'success.main'
                        }}>
                            Duplicate Launch IDs: {stats.duplicateLaunchIDCounts}
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Same Game Name with Different Launch IDs: {stats.sameNameDiffLaunchIDCount}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            <Collapse in={expanded}>
                <Box sx={{ mt: 2 }}>
                    <TextField
                        label="Search duplicates or check missing games"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="Enter game name, Launch ID, Module ID, or Provider"
                        sx={{ mb: 2 }}
                    />

                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="fullWidth"
                    >
                        <Tab label="Duplicate Module-Client Combos" />
                        <Tab label="Duplicate Launch IDs" />
                        <Tab label="Missing Games Check" />
                    </Tabs>

                    <Box sx={{ mt: 2 }}>
                        {activeTab === 0 && renderDuplicateItems(
                            stats.duplicateModuleClientCombos.slice(0, searchTerm ? stats.duplicateModuleClientCombos.length : 20),
                            "Combo"
                        )}

                        {activeTab === 1 && renderDuplicateItems(
                            stats.duplicateLaunchIDs.slice(0, searchTerm ? stats.duplicateLaunchIDs.length : 20),
                            "Launch ID"
                        )}

                        {activeTab === 2 && renderMissingGamesCheck()}
                    </Box>
                </Box>
            </Collapse>
        </Paper>
    );
};

export default GameDiagnostics;